/**
 * ContextLibrary.js
 * Page for managing context documents (upload, view, delete)
 */

import React, { useState, useEffect } from "react";
import { useTeam } from "../context/TeamContext";
import DataService from "../services/DataService";
import {
  FaUpload,
  FaFile,
  FaImage,
  FaTrash,
  FaDownload,
  FaEye,
  FaSearch,
} from "react-icons/fa";

export default function ContextLibrary() {
  const { currentTeam, permissions } = useTeam();
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileDescription, setFileDescription] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");

  const dataService = new DataService();

  useEffect(() => {
    if (currentTeam) {
      loadContexts();
    }
  }, [currentTeam]);

  const loadContexts = async () => {
    try {
      setLoading(true);
      const data = await dataService.getContexts(currentTeam.id);
      setContexts(data);
    } catch (error) {
      console.error("Error loading contexts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      setShowUploadModal(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      await dataService.uploadContext(
        currentTeam.id,
        selectedFile,
        fileName,
        fileDescription,
        []
      );

      setShowUploadModal(false);
      setSelectedFile(null);
      setFileName("");
      setFileDescription("");
      await loadContexts();
      alert("File uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload file: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (contextId) => {
    if (!window.confirm("Are you sure you want to delete this context?")) {
      return;
    }

    try {
      await dataService.deleteContext(currentTeam.id, contextId);
      await loadContexts();
      alert("Context deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete context");
    }
  };

  const filteredContexts = contexts.filter((ctx) => {
    const matchesSearch = ctx.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || ctx.type === filterType;
    return matchesSearch && matchesType;
  });

  const getIconForType = (type) => {
    switch (type) {
      case "screenshot":
      case "image":
        return <FaImage />;
      case "document":
      case "note":
      default:
        return <FaFile />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading context library...</p>
      </div>
    );
  }

  return (
    <div className="context-library-page">
      {/* Header */}
      <div className="page-header">
        <h1>Context Library</h1>
        <p className="page-subtitle">
          Manage your documents, notes, and screenshots
        </p>
      </div>

      {/* Actions Bar */}
      <div className="actions-bar">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search contexts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="document">Documents</option>
            <option value="screenshot">Screenshots</option>
            <option value="note">Notes</option>
            <option value="image">Images</option>
          </select>
        </div>

        {permissions.canWrite && (
          <label className="btn btn-primary upload-button">
            <FaUpload /> Upload Context
            <input
              type="file"
              accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.webp"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
          </label>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowUploadModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Upload Context</h3>
            <p className="modal-subtitle">
              {selectedFile ? selectedFile.name : "No file selected"}(
              {selectedFile ? formatFileSize(selectedFile.size) : "0 B"})
            </p>

            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                className="input"
                placeholder="Enter a descriptive name"
              />
            </div>

            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value)}
                className="textarea"
                rows={3}
                placeholder="Add a description for this context"
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={handleUpload}
                disabled={uploading || !fileName.trim()}
                className="btn btn-primary"
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
              <button
                onClick={() => setShowUploadModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Grid */}
      {filteredContexts.length > 0 ? (
        <div className="context-grid">
          {filteredContexts.map((context) => (
            <div key={context.id} className="context-card">
              <div className="context-icon">{getIconForType(context.type)}</div>
              <div className="context-info">
                <h3 className="context-name">{context.name}</h3>
                {context.description && (
                  <p className="context-description">{context.description}</p>
                )}
                <div className="context-meta">
                  <span className="context-type">{context.type}</span>
                  <span className="context-size">
                    {formatFileSize(context.fileSize)}
                  </span>
                  <span className={`context-status status-${context.status}`}>
                    {context.status}
                  </span>
                </div>
                <div className="context-date">
                  Uploaded{" "}
                  {new Date(
                    context.uploadedAt?.toDate?.() || context.uploadedAt
                  ).toLocaleDateString()}
                </div>
              </div>
              <div className="context-actions">
                {context.downloadURL && (
                  <a
                    href={context.downloadURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-icon"
                    title="Download"
                  >
                    <FaDownload />
                  </a>
                )}
                {permissions.canManage && (
                  <button
                    onClick={() => handleDelete(context.id)}
                    className="btn btn-icon btn-danger"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📁</div>
          <h2>No Contexts Found</h2>
          {searchQuery || filterType !== "all" ? (
            <p>Try adjusting your search or filter criteria</p>
          ) : (
            <>
              <p>Upload documents, screenshots, or notes to get started</p>
              {permissions.canWrite && (
                <label className="btn btn-primary">
                  <FaUpload /> Upload Your First Context
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.webp"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                </label>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
